import { imsGet, imsPost, imsPut } from "../api/api";

/**
 * Product Metrics (Masters Configuration) data layer — IMS endpoints.
 *
 * Paths are relative to IMS_BASE_URL (`${API_BASE_URL}/saas/ims`).
 * Auth headers are attached by the shared API client in src/api/api.js.
 */

const ENDPOINTS = {
  categories: "/product-metrics/categories",
  searchByCategory: "/product-metrics/search/category",
  add: "/product-metrics/add",
  update: (metricId) => `/product-metrics/update/${metricId}`,
};

const SERVICE_CODES = {
  search: "IMS04S",
  add: "IMS04A",
  update: "IMS04U",
};

function unwrapData(response) {
  return response && response.data !== undefined ? response.data : response;
}

/** Session cache + in-flight dedupe — StrictMode remounts must not double-hit the network. */
let categoriesCache = null;
let categoriesInflight = null;

/**
 * GET /saas/ims/product-metrics/categories — IMS04S
 * @returns {Promise<string[]>}
 */
export async function getMetricCategories() {
  if (categoriesCache) return categoriesCache;
  if (categoriesInflight) return categoriesInflight;

  categoriesInflight = (async () => {
    try {
      const response = await imsGet(ENDPOINTS.categories, SERVICE_CODES.search);
      const data = unwrapData(response);
      const list = Array.isArray(data) ? data : [];
      categoriesCache = list;
      return list;
    } catch (err) {
      categoriesCache = null;
      throw err;
    } finally {
      categoriesInflight = null;
    }
  })();

  return categoriesInflight;
}

/**
 * GET /saas/ims/product-metrics/search/category — IMS04S
 *
 * @param {string} category
 * @param {number} [page=0]
 * @param {number} [size=10]
 * @param {string} [sortBy="name"]
 * @param {string} [sortDir="ASC"]
 */
export async function getMetricsByCategory(
  category,
  page = 0,
  size = 10,
  sortBy = "name",
  sortDir = "ASC",
  search = "",
  isActive
) {
  const params = new URLSearchParams({
    category: String(category),
    page: String(page),
    size: String(size),
    sortBy,
    sortDir,
  });

  if (search.trim()) params.set("search", search.trim());
  if (isActive != null) params.set("isActive", String(isActive));

  const response = await imsGet(
    `${ENDPOINTS.searchByCategory}?${params.toString()}`,
    SERVICE_CODES.search
  );

  const data = unwrapData(response) ?? {};
  const content = Array.isArray(data.content)
    ? data.content
    : Array.isArray(data)
      ? data
      : [];

  const pageSize = Number(data.pageSize ?? size) || size;
  const totalElements = Number(
    data.totalElements ?? content.length
  );
  const reportedTotalPages = Number(data.totalPages);
  const totalPages =
    Number.isFinite(reportedTotalPages) && reportedTotalPages > 0
      ? reportedTotalPages
      : Math.max(1, Math.ceil(totalElements / pageSize) || 1);
  const pageNumber = Number(
    data.pageNumber ?? page
  );

  return {
    content,
    pageNumber: Number.isFinite(pageNumber) ? pageNumber : page,
    pageSize,
    totalElements: Number.isFinite(totalElements) ? totalElements : content.length,
    totalPages,
  };
}

/**
 * POST /saas/ims/product-metrics/add — IMS04A
 *
 * @param {{ name: string, abbreviation: string, category: string, description?: string }} payload
 * @returns {Promise<{ message: string, data: object }>}
 */
export async function createMetric(payload) {
  const body = {
    name: String(payload.name ?? "").trim(),
    abbreviation: String(payload.abbreviation ?? "").trim(),
    category: String(payload.category ?? "").trim(),
    description: String(payload.description ?? "").trim(),
  };

  const response = await imsPost(ENDPOINTS.add, body, SERVICE_CODES.add);
  return {
    message: response?.message || "Metric created successfully",
    data: unwrapData(response),
  };
}

/**
 * PUT /saas/ims/product-metrics/update/{metricId} — IMS04U
 *
 * @param {number|string} metricId
 * @param {{ name: string, abbreviation: string, category: string, description?: string, isActive: boolean }} payload
 * @returns {Promise<{ message: string, data: object }>}
 */
export async function updateMetric(metricId, payload) {
  const body = {
    name: String(payload.name ?? "").trim(),
    abbreviation: String(payload.abbreviation ?? "").trim(),
    category: String(payload.category ?? "").trim(),
    description: String(payload.description ?? "").trim(),
    isActive: payload.isActive === true,
  };

  const response = await imsPut(
    ENDPOINTS.update(metricId),
    body,
    SERVICE_CODES.update
  );
  return {
    message: response?.message || "Metric updated successfully",
    data: unwrapData(response),
  };
}
